import React, { createContext, useContext, useCallback, useEffect, useState } from 'react';

/**
 * Lightweight registry that lets detail pages tell the breadcrumb trail the
 * Arabic display name for a raw ID segment in the current URL
 * (e.g. on /admin/courses/<id> the page registers <id> → course title).
 *
 * The provider is mounted inside DashboardLayout, wrapping BOTH <Breadcrumbs />
 * and the page content, so a page (child) can register a name and the trail
 * (sibling) can read it. DashboardLayout mounts per page, so the map is fresh
 * on every navigation — no stale names leak between records.
 */
const BreadcrumbNamesContext = createContext({ names: {}, registerName: () => {} });

export function BreadcrumbNamesProvider({ children }) {
  const [names, setNames] = useState({});

  const registerName = useCallback((key, value) => {
    if (!key || !value) return;
    const k = String(key);
    const v = String(value);
    setNames((prev) => (prev[k] === v ? prev : { ...prev, [k]: v }));
  }, []);

  return (
    <BreadcrumbNamesContext.Provider value={{ names, registerName }}>
      {children}
    </BreadcrumbNamesContext.Provider>
  );
}

export function useBreadcrumbNames() {
  return useContext(BreadcrumbNamesContext);
}

/**
 * Detail-page hook: register the Arabic display name for a URL ID segment so
 * the breadcrumb shows the real record name instead of hiding the ID crumb.
 * Pass a falsy value (e.g. while data is loading) to leave the crumb hidden —
 * the current behaviour for unresolved segments.
 */
export function useBreadcrumbName(key, value) {
  const { registerName } = useBreadcrumbNames();
  useEffect(() => {
    if (key && value) registerName(key, value);
  }, [key, value, registerName]);
}
